import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@/translations';
import { Alert, Checkbox, Form, Modal, Select, Spin } from 'antd';
import {
  EmailNotificationRoleAccess,
  EmailNotificationRoleAccessItem,
  NovuWorkflow,
  NovuWorkflowOption,
  UserTypeRoles,
} from '@/types';

type FormValues = {
  userType?: string;
  userRole?: string;
  workflows?: NovuWorkflow[];
  allowUserModify?: NovuWorkflow[];
};

type EmailNotificationAccessPopupProps = {
  data?: EmailNotificationRoleAccess[];
  userTypeRoles?: UserTypeRoles[];
  workflows?: NovuWorkflowOption[];
  editUserType?: string;
  editUserRole?: string;
  onSubmit(payload: {
    userType: string;
    userRole: string;
    items: EmailNotificationRoleAccessItem[];
  }): Promise<string | undefined>;
  onCancel(): void;
};

export const EmailNotificationAccessPopup: FC<
  EmailNotificationAccessPopupProps
> = ({
  data,
  userTypeRoles,
  workflows,
  editUserType,
  editUserRole,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslate();
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const userType = Form.useWatch('userType', form);
  const selectedWorkflows = Form.useWatch('workflows', form) || [];

  const roleOptions = useMemo(() => {
    const type = userTypeRoles?.find((o) => o.value === userType);
    return (type?.userRoles || []).map((r) => ({
      label: r.text,
      value: r.value,
    }));
  }, [userType, userTypeRoles]);

  const workflowOptions = useMemo(
    () =>
      (workflows || []).map((w) => ({
        label: w.description,
        value: w.workflow,
      })),
    [workflows],
  );

  useEffect(() => {
    setErrorMessage(undefined);
    form.resetFields();
    if (editUserType && editUserRole && data) {
      const rows = data.filter(
        (r) => r.userType === editUserType && r.userRole === editUserRole,
      );
      form.setFieldsValue({
        userType: editUserType,
        userRole: editUserRole,
        workflows: rows.map((r) => r.novuWorkflow),
        allowUserModify: rows
          .filter((r) => r.allowUserModify)
          .map((r) => r.novuWorkflow),
      });
    }
  }, [data, editUserRole, editUserType, form]);

  const handleFinish = async (values: FormValues) => {
    if (!values.userType || !values.userRole) {
      setErrorMessage('Please select user type and role.');
      return;
    }
    setLoading(true);
    setErrorMessage(undefined);
    const allowSet = new Set(values.allowUserModify || []);
    const items = (values.workflows || []).map((w) => ({
      novuWorkflow: w,
      allowUserModify: allowSet.has(w),
    }));
    const error = await onSubmit({
      userType: values.userType,
      userRole: values.userRole,
      items,
    });
    setErrorMessage(error);
    setLoading(false);
    if (!error) onCancel();
  };

  return (
    <Modal
      open
      title={t('emailNotificationAccess')}
      onCancel={onCancel}
      okText={t('save')}
      cancelText={t('cancel')}
      onOk={() => form.submit()}
      confirmLoading={loading}
    >
      <Spin spinning={loading}>
        <Form
          id="email-notification-access-form"
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{ workflows: [], allowUserModify: [] }}
        >
          <Form.Item
            label={t('userType')}
            name="userType"
            rules={[{ required: true }]}
          >
            <Select
              options={(userTypeRoles || []).map((o) => ({
                label: o.name,
                value: o.value,
              }))}
              onChange={() => form.setFieldValue('userRole', undefined)}
              disabled={!!editUserType}
            />
          </Form.Item>

          <Form.Item
            label={t('role')}
            name="userRole"
            rules={[{ required: true }]}
          >
            <Select options={roleOptions} disabled={!!editUserRole} />
          </Form.Item>

          <Form.Item label={t('emailNotificationTypes')} name="workflows">
            <Select
              mode="multiple"
              options={workflowOptions}
              optionFilterProp="label"
              placeholder={t('emailNotificationTypes')}
            />
          </Form.Item>

          {selectedWorkflows.length > 0 ? (
            <Form.Item label={t('allowUserToModify')} name="allowUserModify">
              <Checkbox.Group
                options={selectedWorkflows.map((w) => ({
                  label:
                    workflows?.find((o) => o.workflow === w)?.description || w,
                  value: w,
                }))}
              />
            </Form.Item>
          ) : null}

          {errorMessage ? (
            <Alert message={errorMessage} type="error" className="mb-4" />
          ) : null}
        </Form>
      </Spin>
    </Modal>
  );
};
